import {
    FormControl,
    IconButton,
    InputBase,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    ListSubheader,
    MenuItem,
    Select,
    Stack,
    styled,
    Tooltip,
    Typography
} from "@mui/material";
import _ from 'underscore'
import {allAttachments, allWeapons} from "./WeaponData";
import {buildWeapon, ItemPaper} from "./WeaponBuilder";
import RandomiseIcon from '@mui/icons-material/Casino';
import {lowerOffset, randomInt, useIsMobileHook, useOutsideClick, weaponImageName} from "../utils";
import {displayAttachmentName, displaySlotName} from "./attachments/Attachments";
import RemoveIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import {useEffect, useMemo, useState} from "react";
import {AttachmentEffects} from "./share/AttachmentsSummary";
import WeaponStats from "./WeaponStats";
import ShareLoadout from "./share/ShareLoadout";
import RestartAltIcon from "@mui/icons-material/RestartAlt";

export const weaponsByClass = _.groupBy(allWeapons, (weapon) => weapon.type);

const AttachmentMenu = ({
                            openSlot, //TODO: probably change this
                            setOpenSlot,
                            selectedWeapon,
                            availableAttachments,
                            selectedAttachments,
                            handleAttachmentSelection,
                            graphSettings,
                            flipEffects
                        }) => {
    const [hoveredAttachment, setHoveredAttachment] = useState(null)

    const outsideClick = useOutsideClick(() => setOpenSlot(null))

    const weaponWithHoveredAttachment = useMemo(() => {
        const newAttachments = {...selectedAttachments}
        newAttachments[openSlot] = hoveredAttachment
        return buildWeapon(selectedWeapon, newAttachments, graphSettings).customWeapon
    }, [openSlot, selectedAttachments, hoveredAttachment, selectedWeapon, graphSettings])

    return openSlot != null ?
        <Stack ref={outsideClick} sx={{
            position: 'absolute',
            top: '15px',
            left: '-8px',
            mt: '10px',
            width: '300px',
            borderRadius: '5px',
            backgroundColor: 'background.paper',
            boxShadow: '#02020288 1px 1px 5px 3px',
            zIndex: 5,
            '&::-webkit-scrollbar': {display: 'none'} //TODO: change
        }}>
            <div style={{position: 'relative', maxWidth: 'none', overflowX: 'visible'}}>
                <Stack sx={{
                    position: 'absolute',
                    left: flipEffects ? '-360px' : 'calc(100% + 10px)',
                    top: 0,
                    boxShadow: '#02020288 1px 1px 5px 3px',
                    zIndex: 10,
                    width: '350px'
                }}>
                    <WeaponStats noMargin baseWeapon={selectedWeapon} customWeapon={weaponWithHoveredAttachment}/>
                </Stack>
            </div>
            <List sx={{p: 0, maxHeight: '50vh', overflowY: 'scroll'}}>
                {availableAttachments[openSlot].map((attachment) => (
                    <div style={{}}
                         onMouseEnter={() => setHoveredAttachment(attachment)}
                         onMouseLeave={() => setHoveredAttachment(null)}>
                        <ListItemButton
                            selected={selectedAttachments[openSlot] === attachment}
                            onClick={(e) => {
                                handleAttachmentSelection(attachment, openSlot)
                                setOpenSlot(null)
                                e.stopPropagation()
                            }}
                            sx={{p: 0}}
                        >
                            <ListItem sx={{
                                flexDirection: 'column',
                                alignItems: 'start',
                                m: '5px 10px',
                                p: '10px',
                                backgroundColor: 'rgb(100,100,100,0.1)',
                                borderRadius: '5px',
                                '&:hover': {backgroundColor: 'rgb(100,100,100,0.3)'}
                            }}
                                      key={`item-attachment-${openSlot}-${(displayAttachmentName(attachment))}`}>
                                <ListItemText sx={{alignSelf: 'center', fontSize: '1rem', m: 0}}
                                              primary={displayAttachmentName(attachment)}/>

                                <AttachmentEffects effects={attachment.effects} fontSize='0.7rem'/>
                            </ListItem>
                        </ListItemButton>
                    </div>
                ))}
            </List>
        </Stack> : null
}

export const RandomiseButton = ({label, onClick}) => {
    return <Tooltip title={label} slotProps={lowerOffset}>
        <RandomiseIcon onClick={onClick} sx={{
            transition: 'filter 0.3s ease',
            '&:hover': {
                filter: 'drop-shadow(0px 0px 6px rgb(200 200 200))',
                background: 'none'
            }
        }}/>
    </Tooltip>;
}

const WeaponSelect = styled(InputBase)(({theme}) => ({
    '& .MuiSelect-iconOutlined': {
        color: 'white'
    },
    '& .MuiInputBase-input': {
        fontSize: '1.2rem',
        padding: '5px 10px 0 10px',
        border: 'solid 1px #ddd',
        borderRadius: '10px',
        '&:focus': {
            borderColor: '#ddd',
        },
    },
}));

const defaultSlotPositions = {
    Muzzle: {left: '1%', top: '32%'},
    Barrel: {left: '15%', top: '8%'},
    Front_Rail: {left: '15%', bottom: '35%'},
    Optic: {right: '25%', top: '8%'},
    Stock: {right: '1%', top: '50%'},
    Magazine: {left: '30%', bottom: '8%'},
    Rear_Grip: {right: '20%', bottom: '20%'}
}

const getSlotPosition = (slot, selectedWeapon) => {
    let overrides = {}
    switch (selectedWeapon?.name) {
        case 'MDR':
            overrides = {Magazine: defaultSlotPositions.Rear_Grip, Rear_Grip: defaultSlotPositions.Magazine}
            break
        case 'MP7':
            overrides = {Rear_Grip: {right: '25%', bottom: '22%'}, Magazine: {left: '35%', bottom: '10%'}}
            break
        case 'P90':
            overrides = {
                Muzzle: {left: '1%', top: '50%'},
                Barrel: {left: '8%', top: '35%'},
                Front_Rail: {left: '8%', bottom: '25%'},
                Optic: {left: '35%', top: '10%'},
                Magazine: {left: '55%', top: '30%'},
                Rear_Grip: {left: '40%', bottom: '8%'}
            }
            break
        case 'Vector .45':
            overrides = {
                Barrel: {left: '8%', top: '20%'},
                Front_Rail: {left: '10%', bottom: '35%'},
                Optic: {right: '30%', top: '8%'},
                Magazine: {left: '30%', bottom: '5%'},
                Rear_Grip: {right: '23%', bottom: '18%'}
            }
            break
        case 'M249':
            overrides = {
                Magazine: {left: '35%', bottom: '10%'},
                Rear_Grip: {right: '17%', bottom: '22%'}
            }
            break
        case 'M60':
            overrides = {
                Muzzle: {left: '1%', bottom: '38%'},
                Barrel: {left: '15%', top: '15%'},
                Front_Rail: {left: '18%', bottom: '22%'},
                Magazine: {left: '45%', bottom: '8%'},
                Rear_Grip: {right: '10%', bottom: '22%'}
            }
            break
        case 'M870':
            overrides = {
                Barrel: {left: '15%', top: '12%'},
                Optic: {right: '25%', top: '12%'},
                Magazine: {left: '35%', bottom: '10%'},
                Rear_Grip: {right: '20%', bottom: '23%'}
            }
            break
        case 'AA-12':
            overrides = {
                Magazine: {left: '37%', bottom: '8%'},
            }
            break
        case 'Double-Barreled':
            overrides = {
                Barrel: {left: '15%', top: '15%'},
                Stock: {right: '1%', top: '25%'},
                Rear_Grip: {right: '25%', bottom: '30%'}
            }
            break
        case 'MK 20 SSR':
            overrides = {
                Muzzle: {left: '1%', top: '50%'},
                Barrel: {left: '12%', top: '15%'},
                Front_Rail: {left: '18%', bottom: '29%'},
                Optic: {right: '25%', top: '10%'},
                Magazine: {left: '35%', bottom: '7%'},
                Rear_Grip: {right: '18%', bottom: '18%'}
            }
            break
        case 'SVD':
            overrides = {
                Muzzle: {left: '1%', top: '50%'},
                Barrel: {left: '15%', top: '18%'},
                Front_Rail: {left: '25%', bottom: '28%'},
                Optic: {right: '28%', top: '20%'},
                Magazine: {left: '35%', bottom: '7%'},
                Rear_Grip: {right: '12%', bottom: '18%'}
            }
            break
        case 'M1911':
        case 'M9':
        case 'D50':
            overrides = {
                Muzzle: {left: '1%', top: '42%'},
                Optic: {right: '40%', top: '8%'},
                Magazine: {left: '45%', bottom: '8%'},
                Rear_Grip: {right: '1%', bottom: '35%'}
            }
            break
        case '686 Magnum':
            overrides = {
                Muzzle: {left: '5%', top: '25%'},
                Optic: {right: '35%', top: '8%'},
                Rear_Grip: {right: '10%', bottom: '8%'}
            }
            break
        case '93R':
            overrides = {
                Muzzle: {left: '1%', top: '25%'},
                Optic: {right: '38%', top: '8%'},
                Stock: {right: '1%', top: '40%'},
                Magazine: {left: '45%', bottom: '8%'},
                Rear_Grip: {right: '1%', bottom: '30%'}
            }
            break
        default:
            break
    }
    return {...defaultSlotPositions, ...overrides}[slot]
}

const weaponImageOverrides = {
    P90: {margin: '0 0 0 100px'},
    'Vector .45': {margin: '0 0 0 100px'},
    RPK: {width: '85%'},
    M60: {width: '78%'},
    M870: {width: '75%'},
    'Double-Barreled': {width: '85%'},
    'MK 20 SSR': {width: '80%'},
    SVD: {width: '80%'},
    'TAC-50': {width: '80%'},
    M44: {width: '80%'},
    '686 Magnum': {margin: '0 0 0 100px'},
    '93R': {margin: '0 70px 0 0'}
}

const randomiseAttachments = (weapon, availableAttachments, setSelectedAttachments) => {
    const slots = Object.keys(availableAttachments).filter((key) => availableAttachments[key].length > 0)
    while (slots.length > 5) {
        slots.splice(randomInt(slots.length), 1);
    }

    if (weapon.type === 'Sniper Rifle' && !slots.includes('Optic')) {
        slots[0] = 'Optic'
    }

    const selectedAttachments = {}
    slots.forEach((slot) => selectedAttachments[slot] = availableAttachments[slot][randomInt(availableAttachments[slot].length)])
    setSelectedAttachments({...selectedAttachments})
}

const AttachmentSlot = ({   selectedAttachments,
                            slot,
                            slotsFull,
                            selectedWeapon,
                            openSlot,
                            setOpenSlot,
                            availableAttachments,
                            graphSettings,
                            handleAttachmentSelection,
                            removeAttachment
                        }) => {
    const isSelected = selectedAttachments[slot] != null
    return slotsFull && !isSelected ? null :
        <Stack direction='row'
               onClick={() => {if (!isSelected) setOpenSlot(slot)}}
               sx={{
            alignItems: 'center',
            p: '5px 8px',
            backgroundColor: '#00000088',
            transition: 'all 0.2s ease',
            '&:hover': { backgroundColor: '#22222288', boxShadow: '0px 0px 8px 0px white' },
            borderRadius: '5px',
            position: 'absolute', ...getSlotPosition(slot, selectedWeapon)
        }}>
            <div style={{position: 'relative'}}>
                {
                    openSlot !== slot ? null :
                        <AttachmentMenu openSlot={openSlot}
                                        setOpenSlot={setOpenSlot}
                                        selectedWeapon={selectedWeapon}
                                        availableAttachments={availableAttachments}
                                        selectedAttachments={selectedAttachments}
                                        graphSettings={graphSettings}
                                        handleAttachmentSelection={handleAttachmentSelection}
                                        flipEffects={getSlotPosition(slot, selectedWeapon).right}/>
                }
            </div>
            <Typography sx={{userSelect: 'none'}}>
                {`${displaySlotName(slot)}${isSelected ? ' - ' + displayAttachmentName(selectedAttachments[slot]) : ''}`}
            </Typography>
            {
                isSelected ?
                    <IconButton
                        onClick={() => removeAttachment(selectedWeapon, slot, setOpenSlot)}
                        sx={{p: 0}}>
                        <RemoveIcon sx={{color: 'white', p: '1px 0 0 2px'}}/>
                    </IconButton>
                    :
                    slotsFull ? null :
                        <IconButton onClick={() => setOpenSlot(slot)}
                                    sx={{p: 0}}>
                            <AddIcon sx={{color: 'white', p: '1px 0 0 3px'}}/>
                        </IconButton>

            }
        </Stack>;
}

function NewWeaponBuilder({
                              selectedWeapon,
                              setSelectedWeapon,
                              selectedAttachments,
                              setSelectedAttachments,
                              graphSettings,
                              dialogOpen,
                              setDialogOpen
                          }) {

    const isMobile = useIsMobileHook()
    const [availableAttachments, setAvailableAttachments] = useState([])
    const [openSlot, setOpenSlot] = useState(null)

    const slotsFull = Object.values(selectedAttachments).filter((s) => s != null).length > 4;

    const removeAttachment = (weapon, slot, setOpenSlot) => {
        if (weapon.type === 'Sniper Rifle' && slot === 'Optic') {
            setOpenSlot(slot)
            return
        }
        selectedAttachments[slot] = null
        setSelectedAttachments({...selectedAttachments})
    }

    const handleAttachmentSelection = (attachment, slot) => {
        if (selectedAttachments[slot] === attachment) {
            selectedAttachments[slot] = null
        } else {
            if (slotsFull && selectedAttachments[slot] == null) return
            selectedAttachments[slot] = attachment;
        }
        setSelectedAttachments({...selectedAttachments})
    }

    useEffect(() => {
        if (selectedWeapon?.attachments == null) {
            setAvailableAttachments([]);
            return
        }
        let filteredAttachments = {...allAttachments}
        Object.keys(filteredAttachments)
            .forEach(type => filteredAttachments[type] = filteredAttachments[type].filter((a) => selectedWeapon.attachments.includes(a.id)))
        setAvailableAttachments(filteredAttachments)
    }, [selectedWeapon])


    return (
        <ItemPaper sx={{height: {xs: 'auto', md: '30vh'}}}>
            <Stack sx={{height: '100%'}}>
                <Stack sx={{width: '100%'}} direction='row' justifyContent='space-between'>
                    <Stack direction='row' alignItems='center'>
                        <FormControl sx={{minWidth: 120, m: '2px 5px 5px 5px'}} size="small">
                            <Select
                                labelId="weapon-select-label"
                                id="weapon-select"
                                value={selectedWeapon?.name}
                                onChange={(event) => setSelectedWeapon(event.target.value)}
                                renderValue={() => selectedWeapon?.name ?? "Select Weapon"}
                                displayEmpty
                                sx={{borderRadius: '10px'}}
                                input={<WeaponSelect/>}
                            >
                                {
                                    Object.keys(weaponsByClass).flatMap((type) => [
                                        <ListSubheader key={type} dense
                                                       sx={{lineHeight: '35px', pl: '10px'}}>
                                            {type}
                                        </ListSubheader>,
                                        weaponsByClass[type].map((weapon) =>
                                            <MenuItem key={weapon.name} value={weapon} sx={{
                                                p: '3px 5px 3px 20px',
                                                fontSize: '0.9rem'
                                            }}>{weapon.name}</MenuItem>
                                        )]
                                    )
                                }
                            </Select>
                        </FormControl>
                        <RandomiseButton label='Random Weapon'
                                         onClick={() => setSelectedWeapon(allWeapons[randomInt(allWeapons.length)])}/>
                    </Stack>
                    <Stack direction='row' sx={{mt: '3px'}}>
                        <Stack direction='row'
                               sx={{
                                   display: selectedWeapon == null ? 'none' : null, mr: '20px',
                                   transition: 'filter 0.3s ease',
                                   '&:hover': {
                                       filter: 'drop-shadow(0px 0px 5px rgb(200 200 200))',
                                       background: 'none'
                                   }
                               }}
                               onClick={() => setSelectedAttachments({})}>
                            <Typography sx={{
                                mr: '6px',
                                color: 'white',
                                fontSize: '0.8rem',
                                userSelect: 'none',
                                textAlign: 'center',
                                lineHeight: 1.1
                            }}>Reset{<br/>}Attachments</Typography>
                            <RestartAltIcon/>
                        </Stack>
                        <Stack direction='row'
                               sx={{
                                   display: selectedWeapon == null ? 'none' : null, mr: '35px',
                                   transition: 'filter 0.3s ease',
                                   '&:hover': {
                                       filter: 'drop-shadow(0px 0px 5px rgb(200 200 200))',
                                       background: 'none'
                                   }
                               }}
                               onClick={() => randomiseAttachments(selectedWeapon, availableAttachments, setSelectedAttachments)}>
                            <Typography sx={{
                                mr: '6px',
                                color: 'white',
                                fontSize: '0.8rem',
                                userSelect: 'none',
                                textAlign: 'center',
                                lineHeight: 1.1
                            }}>Random{<br/>}Attachments</Typography>
                            <RandomiseIcon/>
                        </Stack>
                        <Stack direction='row' sx={{
                            display: selectedWeapon == null ? 'none' : null,
                            transition: 'filter 0.3s ease',
                            '&:hover': {
                                filter: 'drop-shadow(0px 0px 5px rgb(200 200 200))',
                                background: 'none'
                            }
                        }}>
                            <ShareLoadout label weapon={selectedWeapon} attachments={selectedAttachments}
                                          open={dialogOpen}
                                          setOpen={setDialogOpen}/>
                        </Stack>
                    </Stack>
                </Stack>
                <Stack sx={{
                    width: '100%',
                    flexGrow: 1,
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative'
                }}>
                    {
                        selectedWeapon?.name == null ? null :
                            <div style={{
                                height: '200px',
                                width: '90%',
                                backgroundSize: 'contain',
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: '50% 50%',
                                backgroundImage: `url(${require('../assets/weapons/' + weaponImageName(selectedWeapon) + '.png')})`,
                                ...weaponImageOverrides[selectedWeapon.name]
                            }}/>
                    }
                    {
                        Object.keys(availableAttachments)
                            .filter((slot) => availableAttachments[slot]?.length > 0)
                            .map((slot) => <AttachmentSlot selectedAttachments={selectedAttachments} slot={slot} slotsFull={slotsFull} selectedWeapon={selectedWeapon} openSlot={openSlot} setOpenSlot={setOpenSlot} availableAttachments={availableAttachments} graphSettings={graphSettings} handleAttachmentSelection={handleAttachmentSelection} removeAttachment={removeAttachment} />)
                    }
                </Stack>
            </Stack>
        </ItemPaper>
    )
}

export default NewWeaponBuilder;
